import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { getInterviewerImageSrc } from '../hero/interviewers';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

const MESSAGES = [
  { key: 'm1', speaker: 'interviewer' },
  { key: 'm2', speaker: 'me' },
  { key: 'm3', speaker: 'follow_up' },
  { key: 'm4', speaker: 'me' },
  { key: 'm5', speaker: 'follow_up' },
] as const;

const AVATAR_SIZE = 44;

/** STEP 3 — 꼬리질문. 면접관(회색) → 내 답변(검정) → 꼬리질문(민트) 대화 */
export const FollowUpChat = () => {
  const { t } = useTranslation();
  const avatarSrc = getInterviewerImageSrc('hyeongseok');

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-chat-title">
      <Box css={styles.inner}>
        <SectionHead
          id="ai-chat-title"
          step={3}
          titleKey="ai_interviews.landing.chat.title"
          descriptionKey="ai_interviews.landing.chat.desc"
        />

        <Box component="ol" css={styles.chat}>
          {MESSAGES.map((message) => {
            const mine = message.speaker === 'me';
            return (
              <Box
                key={message.key}
                component="li"
                css={[styles.message, mine ? styles.messageMine : null]}
              >
                {mine ? null : (
                  // eslint-disable-next-line @next/next/no-img-element -- 면접관 CDN 캐릭터
                  <img
                    src={avatarSrc}
                    alt=""
                    width={AVATAR_SIZE}
                    height={AVATAR_SIZE}
                    css={styles.avatar}
                  />
                )}
                <Box
                  css={[
                    styles.bubble,
                    mine ? styles.bubbleMine : null,
                    message.speaker === 'follow_up' ? styles.bubbleFollowUp : null,
                  ]}
                >
                  <small
                    css={[
                      styles.label,
                      message.speaker === 'follow_up' ? styles.labelFollowUp : null,
                    ]}
                  >
                    {t(`ai_interviews.landing.chat.${message.key}.label`)}
                  </small>
                  <Text component="span" css={styles.text}>
                    {t(`ai_interviews.landing.chat.${message.key}.text`)}
                  </Text>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    background: '#fff',
    padding: `${LANDING_SPACE.sectionY} ${LANDING_SPACE.pageX}`,
  }),
  inner: css({
    maxWidth: 1200,
    margin: '0 auto',
  }),
  chat: css({
    maxWidth: 960,
    margin: `${LANDING_SPACE.blockTop} auto 0`,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    [LANDING_MOBILE_MQ]: {
      gap: 12,
    },
  }),
  message: css({
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    maxWidth: '86%',
    [LANDING_MOBILE_MQ]: {
      maxWidth: '100%',
      gap: 8,
    },
  }),
  messageMine: css({
    alignSelf: 'flex-end',
    [LANDING_MOBILE_MQ]: {
      paddingLeft: 32,
    },
  }),
  avatar: css({
    flexShrink: 0,
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: '50%',
    background: '#AFC7E4',
    objectFit: 'cover',
    objectPosition: 'center 12%',
    [LANDING_MOBILE_MQ]: {
      width: 32,
      height: 32,
    },
  }),
  bubble: css({
    padding: '20px 22px',
    borderRadius: '18px 18px 18px 6px',
    background: LANDING_COLOR.surface,
    color: LANDING_COLOR.ink,
    [LANDING_MOBILE_MQ]: {
      padding: '14px 16px',
    },
  }),
  bubbleMine: css({
    borderRadius: '18px 18px 6px 18px',
    background: LANDING_COLOR.ink,
    color: '#fff',
  }),
  bubbleFollowUp: css({
    background: LANDING_COLOR.mint,
    color: '#062B24',
  }),
  label: css({
    display: 'block',
    marginBottom: 4,
    color: LANDING_COLOR.subtle,
    fontSize: LANDING_FONT.caption,
    fontWeight: 800,
  }),
  labelFollowUp: css({
    color: 'rgba(6,43,36,.65)',
  }),
  text: css({
    color: 'inherit',
    fontSize: LANDING_FONT.introBody,
    fontWeight: 600,
    lineHeight: 1.55,
    wordBreak: 'keep-all',
  }),
};
